import create, { UseStore } from 'zustand'
import { emptyNotes, notesApi, UseNotesApi } from '../components/common/notes'
import { createEditApi, EditStore } from '../components/trucode/edits'
import { alertApi, AlertStore } from './alerts'
import { auth } from './auth'
import { userApi, UserApi } from './cache-manager'
import { loadCritCareCpts } from './codesets'
import { dateFromInput, DateInput, isoDateTimeString } from './date-time'
import { documentResolver } from './document-resolver'
import {
  worksheetResources,
  WorksheetResources,
  worksheetSelection,
  WorksheetSelectionApi,
} from './worksheet-selection-api'

import { FormApi, setModifiers, sortedByDateLex } from '.'
import { ownerForm } from '../components/coding-tool/owner-form'
import { reasonForVisitApi, ReasonForVisitApi } from '../components/reason-for-visit/reason-for-visit-api'
import { fetchRequest } from './'
import { checkAlertStatus } from './alerts/alerts-api'
import { getRebuttalAction, RebuttalAction } from './audit-rebuttal/audit-rebuttal-model'
import { documentApi2 } from './document-viewer/document-api'
import {
  auditRecord,
  CoderNote,
  CoderReviewQuestion,
  completeState,
  DownCode,
  initRebuttalCache,
  LocationDepartmentAccount,
  LostRevenue,
  NCNBReason,
  Provider,
  ReturnItem,
  TrackingCode,
  Visit,
  Worksheet,
  WorksheetComponents,
  WorksheetStateAction,
  WorksheetStateInfo,
  WorksheetSummary,
} from './domain'
import { DownCodeApi, downCodeApi } from './downcode-api'
import { createEmCalculatorApi, EmCalculatorStore } from './em-calculator-api'
import { hospitalistContext, HospitalistContext } from './hospitalist-context'
import { IcdCptApi, icdCptApi } from './icd-cpt-api'
import { log } from './log'
import { LostRevenueApi, lostRevenueApi } from './lost-revenue-api'
import { messageApi } from './message-api'
import { ncnbReasonsApi, NcnbReasonsApi } from './ncnb-reasons-api'
import { ndcApi, NdcApi } from './ndc'
import { returnApi, ReturnApi } from './return-api'
import { reviewQuestionApi, ReviewQuestionApi } from './review-question-api'
import { history } from './router'
import { createStatusApi, StatusApi } from './status'
import { showSuccessMessage, showToast } from './toast'
import { TrackingCodeApi, trackingCodeApi } from './tracking-code-api'
import {
  checkoutTransition,
  getWorksheet,
  transitionApi,
  TransitionPayload,
  TransitionResult,
  TransitionStore,
  WorksheetStateRef,
} from './transition-api'
import {
  endOfVisitListError,
  getLastWorksheetId,
  notCodeableError,
  noVisitEntriesError,
  setLastWorksheetId,
  worksheetQueue,
} from './visit-queue'
import { currentWorkflow, findFeature, getActivity, isAlertsV2, isReviewWorkflowState, isReviewWorkflowTransitionState, workflows } from './workflow-api'
import { worksheetMessage, WorksheetResult } from './worksheet-service'
import { normalizeServiceDates, setWorksheetAuditInfo, setWorksheetTransients } from './worksheets'
import { locationProviders } from './provider-search-requests'

export const visitPrefix = 'currentVisit'
function visitKey(visitId: string) {
  return `${visitPrefix}_${visitId}`
}

const service = 'codingconfig'

type Flag = 'No' | 'Yes' | 'Optional'

export type BaseCode = {
  code: number | string
  description?: string
}

export type VisitCode = BaseCode & {
  returnText?: string
  requireDate?: Flag
  startDateTime?: Flag
  endDateTime?: Flag
  provider?: Flag
}
export interface CodingToolParameters {
  bypassFinalizeOwnerValidation?: Flag
  secondaryProvider?: Flag
  tertiaryProvider?: Flag
  eMCalculator?: Flag
}

type LabelValue = {
  label: string
  value: string | number
}

export interface CodingConfig {
  presentOnAdmission: BaseCode[]
  providers: Provider[]
  primaryProviders?: Provider[]
  secondaryProviders?: Provider[]
  tertiaryProviders?: Provider[]
  locationCode?: string
  departmentCode?: string
  downcodeList?: VisitCode[]
  ncnbList?: VisitCode[]
  emCodes?: VisitCode[]
  lostRevenueList?: VisitCode[]
  arrivalModeList?: VisitCode[]
  arrivalModeOptions?: LabelValue[]
  dispositionList?: VisitCode[]
  dispositionOptions?: LabelValue[]
  trackingCodesList?: VisitCode[]
  accidentList?: VisitCode[]
  accidentOptions?: LabelValue[]
  codingToolParameters?: CodingToolParameters
  returnReasonsList?: ReturnItem[]
  codeReviewQuestionList: any[]
  auditCPT: VisitCode[]
  auditCPTMod: VisitCode[]
  auditICD: VisitCode[]
  auditDownCode: VisitCode[]
  auditNCNB: VisitCode[]
  auditLostRevenue: VisitCode[]
  auditSendBack: VisitCode[]
  auditTracking: VisitCode[]
  cancelCodes?: BaseCode[]
  workflowStateMap: {
    [key: string]: { name: string; label: string }[]
  }
  placeOfService?: VisitCode[]
  cptLinks?: boolean
  emLinks?: boolean
}

interface CodingConfigArgs extends LocationDepartmentAccount {
  worksheetTypes?: string[]
  workflowNames?: string[]
}

export type CodingConfigResult = WorksheetResult<CodingConfig>

export const getCodingConfigRequest = fetchRequest<CodingConfigArgs, CodingConfigResult>({
  method: 'post',
  body: (args) =>
    worksheetMessage(
      {
        locationCode: args.location,
        departmentCode: args.department,
        worksheetTypes: args.worksheetTypes,
        workflowNames: args.workflowNames,
      },
      'getcodingconfig',
      service,
    ),
})

export type WorksheetContext = WorksheetStateInfo & {
  worksheet: Worksheet
  status: StatusApi
  notesApi: UseNotesApi
  downCodes: DownCode[]
  ncnbReasons: NCNBReason[]
  lostRevenue: LostRevenue[]
  tracking: TrackingCode[]
  returns: ReturnItem[]
  coderNotes: CoderNote[]
  coderReviewQuestions: CoderReviewQuestion[]
  alerts?: AlertStore
  config?: CodingConfig
  ndc?: NdcApi
  readonly?: boolean
  hospitalist?: boolean
  selection?: WorksheetSelectionApi
  calculator?: EmCalculatorStore
  workflow?: string
  secondaryEnabled?: boolean
  tertiaryEnabled?: boolean
  inlineEdit?: boolean
  rebuttal?: RebuttalAction
  visit?: Visit
  getServiceDate?: () => Date
  onTransitionComplete?: (a: WorksheetStateRef) => void
}

export interface WorksheetApis {
  context: WorksheetContext
  downCode: DownCodeApi
  ncnbReasons: NcnbReasonsApi
  lostRevenue: LostRevenueApi
  trackingCode: TrackingCodeApi
  reviewQuestions: ReviewQuestionApi
  returns: ReturnApi
  icdCpt: IcdCptApi
  reasonForVisit: ReasonForVisitApi
  transition: TransitionStore
  alerts: AlertStore
  edits: EditStore
  ownerForm?: FormApi<{ owner: Provider }>
  setPhysician?: (provider: Provider) => void
  onTransitionComplete: (a: WorksheetStateAction) => void
}

export interface VisitContext {
  visit: Visit
  notesApi: UseNotesApi
  worksheetTypes: string[]
  worksheets: WorksheetApis[]
  config: CodingConfig
  arrival: string
  disposition: string
  injury: string
  injuryDate: DateInput
  injuryState: string
  hasAuditSummary?: boolean | unknown
  showPreviousVisits?: boolean
  users?: UserApi
  readonly?: boolean
  hospitalist?: boolean
  workflowNames?: string[]
  hospitalistContext?: HospitalistContext
  data?: UseStore<{ contextChanged: boolean }>
  activity?: string
  alertsV2: boolean
  getApiById?: (id: string) => WorksheetApis
  update?: (selected: string[]) => VisitContext
  refreshRouteApis?: () => void
  getOpenTransition?: () => TransitionStore
  onTransitionDialogueComplete?: (a: WorksheetStateRef) => void
}

const noConfigurationError = new Error('Error loading configuration')
const noWorksheetsAvailable = new Error('Error no worksheets available')

export function hospitalistEnabled(a: { locationCode: string; readonly?: boolean }, enable = true) {
  if (a.readonly) {
    return false
  }
  return enable || (a?.locationCode || '').toLowerCase().indexOf('summit') === -1
}

async function visitContext(visit: Visit, worksheetId?: string): Promise<VisitContext> {
  let editingSummaries = visit.wsSummaries.slice()

  setModifiers(visit.locationCode, visit.serviceDate)
  loadCritCareCpts()

  if (worksheetId && visit.readonly) {
    editingSummaries = editingSummaries.filter((a) => a.worksheetId === worksheetId)
  } else if (editingSummaries.length > 1 && isReviewWorkflowTransitionState()) {
    sortedByDateLex<WorksheetSummary>(editingSummaries, (a: WorksheetSummary) => a.serviceDate as string, true)
    const lastWorksheetId = getLastWorksheetId()
    if (lastWorksheetId) {
      let lastEditable = editingSummaries.find((a) => a.worksheetId === lastWorksheetId)

      let firstEditable: WorksheetSummary
      if (lastEditable) {
        for (let i = editingSummaries.indexOf(lastEditable) + 1; i < editingSummaries.length; i++) {
          if (editingSummaries[i].editable) {
            firstEditable = editingSummaries[i]
            break
          }
        }
      }

      if (!firstEditable) {
        firstEditable = editingSummaries.find((a) => a.editable)
      }
      editingSummaries = firstEditable ? [firstEditable] : []
    }
  }

  const workflow = currentWorkflow()

  const hospitalist =
    !worksheetId && hospitalistEnabled(visit, !!editingSummaries.find((a) => a.departmentCode === 'HOSPITALIST'))

  let resources: WorksheetResources
  let editingComponents: TransitionPayload

  const data = create(() => ({ contextChanged: false }))

  if (hospitalist) {
    resources = await worksheetResources(editingSummaries, workflow)

    worksheetQueue.getState().setVisit(visit)

    editingSummaries = resources.tabSummaries || editingSummaries
    editingComponents = {
      visitId: visit.visitId,
      worksheetComponents: resources.tabComponents,
      arrivalMode: resources?.arrivalMode,
      disposition: resources.disposition,
      injury: resources.injury,
    }
  }

  visit.worksheetId = editingSummaries[0].worksheetId
  setLastWorksheetId(visit.worksheetId)
  checkForExistingWorksheet(visit.worksheetId );


  documentResolver.openWindowAndValidate(visit)

  const worksheetIds = editingSummaries.map((v) => v.worksheetId)
  if (!worksheetIds.length) {
    throw new Error('Visit must return at least one editable worksheet. Account: ' + visit.accountNumber)
  }
  const activity = getActivity()
  const rebuttal = getRebuttalAction(activity)
  const readonly = visit?.readonly || !!rebuttal || false

  const summary = visit?.wsSummaries[0]
  const department = summary.departmentCode || 'ED'

  const emFeature = findFeature('EMCalculator')

  //TODO: feature params should be JSON
  const required = emFeature?.parameters && JSON.stringify(emFeature?.parameters || {}).indexOf('force') > -1
  const alertsV2 = isAlertsV2(visit.locationCode)
  // console.log('context config', department, summary);

  await workflows.actions.load({
    location: visit.locationCode,
    department,
    workflow,
    preloadDepartments: editingSummaries.map((a) => a.departmentCode),
  })

  const worksheetTypes = editingSummaries.map((v) => v.worksheetType)

  const values: [TransitionResult, CodingConfigResult] = await Promise.all([
    worksheetId
      ? getWorksheet({ worksheetId })
      : editingComponents
      ? (Promise.resolve({
          result: editingComponents,
        }) as Promise<TransitionResult>)
      : checkoutTransition(worksheetIds),
    getCodingConfigRequest({
      location: visit.locationCode,
      department,
      worksheetTypes: visit?.wsSummaries.map((a) => a.worksheetType),
      workflowNames: [workflow],
    }),
  ])

  const v = values[0]
  const apiSupportsSuccess = v && v.hasOwnProperty('success')

  if (!v) {
    showToast(`Could not checkout worksheet components for ${worksheetIds.join(',')} ${isoDateTimeString(new Date())}`)
    throw noConfigurationError
  } else if ((apiSupportsSuccess && !v.success) || !v?.result?.worksheetComponents?.length) {
    throw noWorksheetsAvailable
  }

  const transition: TransitionPayload = v.result
  const components: WorksheetComponents[] = transition.worksheetComponents
  if (!components?.length || !components[0]) {
    throw noWorksheetsAvailable
  }
  const audit: Record<string, unknown> = {}
  setWorksheetTransients(components, audit, workflow)

  const config: CodingConfig = values[1].result

  /*
  For USACS:
  Primary Provider will always be either a MD or DO
  Secondary Provider will always be anything OTHER than a MD or DO (FNP, PA-C, DMS PA-C, etc.)
  Tertiary Provider for USACS is utilized for attribution rules and will be the same as secondary 
*/
  let allProviders = await locationProviders(visit.locationCode, config.departmentCode) // config.providers

  if (!allProviders?.length) {
    log.error('No providers found for location. Using Redis config providers. ', visit.locationCode)
    allProviders = config.providers
  }
  
  if (visit.locationCode.includes('USACS')) {
    config.primaryProviders = allProviders.filter((a) => a.degree === 'MD' || a.degree === 'DO')
    config.secondaryProviders = allProviders.filter((a) => a.degree !== 'MD' && a.degree !== 'DO')
    config.tertiaryProviders = config.secondaryProviders
  } else {
    config.primaryProviders = allProviders
    config.secondaryProviders = allProviders
    config.tertiaryProviders = allProviders
  }

  const usac = visit.locationCode.includes('USACS')
  if (usac) {
    config.arrivalModeOptions = (config.arrivalModeList || [])
      .slice(0, 3)
      .map((a) => ({ label: a.description, value: a.code }))
    config.dispositionOptions = (config.dispositionList || [])
      .slice(0, 3)
      .map((a) => ({ label: a.description, value: a.code }))
    config.accidentOptions = (config.accidentList || [])
      .slice(0, 3)
      .map((a) => ({ label: a.description, value: a.code }))
  }

  let arrival = null
  let disposition = null
  let injury = null

  const { arrivalMode, disposition: disp, injury: inj } = transition || ({} as any)
  arrival = arrivalMode ? arrivalMode.code : null
  disposition = disp ? disp.code : null
  injury = inj ? inj.code : null

  const injuryDate: DateInput = inj ? inj.injuryDate : null
  const injuryState: string = inj ? inj.injuryState : null

  const ids = {}

  components.forEach((c) => {
    const w = c.worksheet
    ids[w.worksheetType] = w.worksheetId
  })

  const notes = notesApi({ notes: emptyNotes(worksheetTypes), ids })

  let showPreviousVisits = false

  let apisById: Record<string, WorksheetApis> = {}

  const refreshRouteApis = () => {
    data.setState({ contextChanged: !data.getState().contextChanged })
  }
  function getOpenTransition() {
    const list = getApis()
    let api = null
    list.forEach((a) => {
      const open = a.transition.getState().openTransitionDialogue
      if (open) {
        api = a.transition
      }
    })

    if (!api && hc) {
      const id = hc.data.getState().selectedId
      if (id) {
        api = getApiById(id).transition
      }
    }
    return api
  }
  let pendingTransition = false

  const { secondaryProvider, tertiaryProvider } = config.codingToolParameters
  const secondaryEnabled = secondaryProvider && secondaryProvider !== 'No'
  const tertiaryEnabled = tertiaryProvider && tertiaryProvider !== 'No'

  const inlineEdit = activity === 'CODING'

  function getApi(c: WorksheetComponents) {
    if (!c) {
      throw 'Null worksheet components'
    }

    showPreviousVisits = showPreviousVisits || !!c?.worksheet?.pastWorksheets?.length

    return worksheetApis({
      hospitalist,
      readonly,
      rebuttal,
      workflow,
      secondaryEnabled,
      tertiaryEnabled,
      inlineEdit,
      selection:
        resources &&
        worksheetSelection({
          visit,
          onSelected,
          refreshRouteApis,
          getApiById: getApiById,
          resources: { ...resources },
          worksheet: c,
        }),
      worksheet: c.worksheet,
      stateInfo: c.stateInfo,
      status: createStatusApi(),
      notesApi: notes,
      calculator: calculator(
        readonly,
        required,
        c.worksheet.locationCode,
        c.worksheet.departmentCode,
        c.worksheet.serviceDate,
      ),
      alerts: alertApi({
        deferConnection: hospitalist || readonly,
        worksheetId: c.worksheet.worksheetId,
        alerts: c.alerts,
      }),
      downCodes: c.downCodes,
      ncnbReasons: c.ncnbReasons,
      lostRevenue: c.lostRevenues,
      tracking: c.worksheetTrackingCodes,
      coderNotes: c.coderNotes,
      returns: c.sendbacks,
      coderReviewQuestions: c.coderReviewQuestions,

      ndc: ndcApi({
        locationCode: visit.locationCode,
        departmentCode: c.worksheet.departmentCode,
        worksheetType: c.worksheet.worksheetType,
      }),
      config,
      visit,
    })
  }
  function getApiById(id: string): WorksheetApis {
    if (!id) {
      debugger
      throw 'Null worksheet id'
    }
    if (apisById[id]) {
      return apisById[id]
    }
    const c = resources.components[id]
    const api = getApi(c)
    apisById[id] = api
    return api
  }
  function getApis(): WorksheetApis[] {
    return Object.values(apisById)
  }
  let worksheets: WorksheetApis[]
  let hc: HospitalistContext

  function onSelected(c: WorksheetComponents) {
    const { worksheetId, stateInfo, worksheet } = c
    const api = getApiById(worksheetId)

    const onComplete = () => {
      if (hc) {
        if (!hc.data.getState().lastWorksheet) {
          let selectedIds = [worksheetId]
          if (worksheets?.length > 1) {
            selectedIds = worksheets.map((a) => a.context.selection.data.getState().selected.worksheetId)
          }

          visitContextApi.getState().updateWorksheets(selectedIds)

          const summary = resources.summaryById[worksheetId]
          hc.updateDocuments(worksheetId, summary)

          api.alerts.setState({ alerts: c.alerts || [] })
          checkAlertStatus()
        }
      }
    }
    const state = stateInfo?.name || worksheet.state
    const notCheckedOut = completeState(state)
    if (notCheckedOut) {
      if (!pendingTransition) {
        pendingTransition = true
        const action = c.stateInfo.actions.find((a) => a.action === 'CHECKOUT') || ({ action: 'CHECKOUT' } as any)

        api.transition
          .getState()
          .transition(action, () => {
            pendingTransition = false
            const stateInfo = api.transition.getState().worksheetState.stateInfo
            api.context.selection.updateSummary({ stateInfo }, worksheetId)
            hc.buildTuples(worksheetId)
            onComplete()
          })
          .catch((err) => {
            pendingTransition = false
            showToast(err || 'Transition Error')
          })
      }
    } else {
      onComplete()
    }
  }

  function update(selected: string[]): VisitContext {
    worksheets = hospitalist ? selected.map((id) => getApiById(id)) : components.map((c) => getApi(c))

    if (hospitalist) {
      hc = hospitalistContext({
        visit,
        worksheets,
        getApiById,
        getApis,
        resources,
        onComplete: () => {
          hc.destroy()
          worksheets = []
          data.setState({ contextChanged: !data.getState().contextChanged })
          loadNextVisit(visit)
        },
      })

      worksheets.forEach((a) => {
        a.alerts.getState().connect()
        a.context.onTransitionComplete = hc.updateSummary
      })
      hc.buildTuples(selected[0], true)
    }

    const hasAuditSummary = audit.hasAuditSummary

    console.info('hasAuditSummary', audit.hasAuditSummary)

    const onTransitionDialogueComplete = (a: WorksheetStateRef) => {
      if (hc) {
        hc.quit(a).then(() => navigateToList(visit))
      } else {
        navigateToList(visit)
      }
    }

    return {
      readonly,
      visit,
      worksheetTypes,
      worksheets,
      config,
      arrival,
      disposition,
      injury,
      injuryDate,
      injuryState,
      hasAuditSummary,
      showPreviousVisits,
      data,
      activity,
      hospitalist,
      hospitalistContext: hc,
      alertsV2,
      workflowNames: workflows.actions.allWorkflows().map((a) => a.name),
      notesApi: notes,
      users: userApi(config.providers),
      getApiById,
      onTransitionDialogueComplete,
      refreshRouteApis,
      getOpenTransition,
    }
  }
  const ctx = update(worksheetIds)
  ctx.update = update
  return ctx
}

export function worksheetApis(context: WorksheetContext): WorksheetApis {
  const {
    status,
    worksheet,
    coderNotes,
    notesApi,
    stateInfo,
    alerts,
    hospitalist,
    selection,
    calculator,
    config,
    readonly,
    workflow,
    visit,
  } = context
  const { worksheetId } = worksheet

  worksheet.auditInfo = auditRecord(workflow, worksheet.auditInfo)
  initRebuttalCache(workflow)

  const icdCpt = icdCptApi(context)
  icdCpt.calculator = calculator

  const reasonForVisit = reasonForVisitApi({ context, icdCpt })

  let success = false

  function showError(obj: any) {
    const message = (obj || 'There was an error processing this request').toString()
    if (message.length < 300) {
      status.setState({ status: message })
    } else {
      showToast(message)
    }
  }

  const saveWorksheet = async (validate = false) => {
    const runValidation = validate && !icdCpt.getValidated()
    if (runValidation) {
      return new Promise((resolve) => {
        icdCpt.validate((hasAlerts) => {
          resolve(!hasAlerts)
        })
      }) as any
    }

    const sheet = icdCpt.appendInactive(icdCpt.current())

    if (isReviewWorkflowState()) {
      sheet.downCodes = api.downCode.list.data.getState().all
      sheet.lostRevenues = api.lostRevenue.list.data.getState().all
      sheet.trackingCodes = api.trackingCode.list.data.getState().all
      setWorksheetAuditInfo(sheet, workflow)
    }

    status.getState().setStatus(null)
    success = false

    try {
      let result: SaveWorksheetResult = await saveWorksheetRequest(sheet)

      const alertList = result?.result?.alerts
      alerts.setState({ alerts: alertList || [] })

      if (!result.success) {
        success = false
        showError((result.errors || ['Save Error']).join(' '))
        return Promise.resolve(success)
      }

      success = true
    } catch (error) {
      showError(error)
    }
    return Promise.resolve(success)
  }

  const transition = transitionApi({
    alerts,
    status,
    worksheetState: { worksheetId, stateInfo },
    transitionGuard: async (a) => {
      // console.log('transitionGuard', a);
      const saveSuccess = await saveWorksheet(a.action === 'FINISHEDCODING')
      return Promise.resolve(saveSuccess)
    },
  })
  transition.setState({ worksheet })

  if (coderNotes && coderNotes.length) {
    notesApi.useNotes.getState().setNotes(worksheet.worksheetType, coderNotes)
  }

  const edits = createEditApi({ icdCpt })

  function getServiceDate() {
    const date: Date = icdCpt.form.controls.serviceDate.value.getState().value || new Date()
    return date
  }
  context.getServiceDate = getServiceDate

  const onTransitionComplete = (a: WorksheetStateAction) => {
    alerts.getState().close()
    showSuccessMessage(`Transition ${a?.action} Successful`)
    setCurrentWorksheet(undefined)

    if (hospitalist && selection) {
      const s = transition.getState().worksheetState
      selection.updateSummary(s)

      if (context.onTransitionComplete) {
        context.onTransitionComplete(s)
      }
      // TODO: reset tab
    } else {
      loadNextVisit(visit)
    }
  }

  const api: WorksheetApis = {
    context,
    icdCpt,
    reasonForVisit,
    transition,
    alerts,
    edits,
    onTransitionComplete,
    downCode: downCodeApi(context),
    ncnbReasons: ncnbReasonsApi(context),
    lostRevenue: lostRevenueApi(context),
    trackingCode: trackingCodeApi(context),
    reviewQuestions: reviewQuestionApi(context),
    returns: returnApi(context),
  }
  api.setPhysician = (provider: Provider) => {
    const { icdCpt, downCode, lostRevenue, trackingCode, returns, ncnbReasons } = api
    // Each sub-api keys off a different identifier: icd/cpt, downcode and
    // lost-revenue match on providerId, tracking-code on userId, returns on login.
    icdCpt.setPhysician(provider.providerId)
    downCode.setPhysician(provider.providerId)
    ncnbReasons.setPhysician(provider.providerId)
    lostRevenue.setPhysician(provider.providerId)
    trackingCode.setPhysician(provider.userId)
    returns.setPhysician(provider.providerId, provider.login)
  }

  if (hospitalist) {
    api.ownerForm = ownerForm(
      config.primaryProviders,
      worksheet,
      (a: Provider) => {
        if (a?.providerId) {
          api.setPhysician(a)
        }
      },
      readonly,
    )
  }

  return api
}

type VisitContextApi = {
  context: VisitContext
  worksheets: WorksheetApis[]
  loading: boolean
  updateWorksheets(ids: string[]): void
  load(visit?: Visit, worksheetId?: string): void
}
export let visitRetries = 0

// A visit the queue auto advanced away from because it could not be opened.
// Cleared as soon as another visit loads, or once the failure has been reported.
let skippedVisit: { visit: Visit; error: Error } = null

function skippedVisitMessage() {
  if (!skippedVisit) {
    return 'You have reached the end of the visit list.'
  }
  const { visit, error } = skippedVisit
  skippedVisit = null

  log.error('unable to open visit', error, visit?.locationCode, visit?.accountNumber, visit?.worksheetId)

  const account = visit?.accountNumber ? ` for account ${visit.accountNumber}` : ''
  return `No worksheets could be opened${account}. ${error?.message || error} There are no other visits in the list.`
}

function calculator(
  readonly: boolean,
  required: boolean,
  locationCode: string,
  department: string,
  serviceDate: string,
) {
  const api = createEmCalculatorApi()
  const year = dateFromInput(serviceDate).getFullYear()

  const v2 = year > 2022

  console.info('creating em calculator... v2', v2, department)
  api.setState({ v2, readonly, required })
  api.getState().loadConfig(locationCode, department)
  return api
}

type OrganizationState = {
  departments: any[]
  locations: any[]
}

export const organizationData = create<OrganizationState>(() => ({
  departments: [],
  locations: [],
}))

function createVisitContextApi(): UseStore<VisitContextApi> {
  return create<VisitContextApi>((set, get) => ({
    context: null,
    loading: false,
    worksheets: null,
    updateWorksheets: (ids) => {
      if (ids?.length) {
        const ctx = get().context
        const update = ctx.update(ids)

        ctx.hospitalistContext = update.hospitalistContext

        set({ worksheets: update.worksheets })
      }
    },
    load: async (visit: Visit, worksheetId?: string) => {
      set({ loading: true, context: null, worksheets: [] })

      const readonly = visit?.readonly || false

      try {
        if (!visit) {
          visit = await worksheetQueue.getState().nextVisit()
        } else {
          worksheetQueue.getState().setVisit(visit)
        }

        visit.readonly = readonly
        const context = await visitContext(visit, worksheetId)

        messageApi.actions.update(context)
        log.info('visit loaded', context?.visit?.locationCode, context?.visit?.accountNumber)
        set({ context, loading: false, worksheets: context.worksheets })
        visitRetries = 0
        skippedVisit = null
      } catch (error) {
        set({ loading: false })

        if (error === notCodeableError) {
          log.info('not codeable, loading next visit...')
          loadNextVisit(visit, error)
          return
        }

        if (error === noWorksheetsAvailable) {
          log.info('no worksheets available, loading next visit...')
          loadNextVisit(visit, error)
          return
        }

        if (error === noVisitEntriesError) {
          log.info('no visit entries')
        } else if (error === endOfVisitListError) {
          navigateToList(skippedVisit?.visit || visit)
          showToast(skippedVisitMessage())
        } else if (error === noConfigurationError) {
          navigateToList(visit)
        } else {
          visitRetries++

          if (auth.isAuthenticated() && visitRetries < 1) {
            get().load()
          } else {
            navigateToList(visit)

            log.error('loading visit failed', error, visit)

            showToast('Loading visit failed' + error)
          }
        }
      }
    },
  }))
}

export function navigateToList(existing: Visit) {
  removeCurrentVisitSession(existing)
  setCurrentWorksheet(null);
  documentApi2.closeWindow()
  history.push('/list')
}

export function setCurrentVisitSession(visit: Visit, updateQueue = true, worksheetId: string = null) {
  localStorage.setItem(visitKey(visit.visitId), JSON.stringify(visit))

  if (updateQueue) {
    worksheetQueue.getState().setVisit(visit)
    visitContextApi.getState().load(visit, worksheetId)
  }
}


const WS = '_ws'

export function setCurrentWorksheet(worksheetId: string) {
  localStorage.setItem(WS, worksheetId);
}
export function getCurrentWorksheet() {
  return localStorage.getItem(WS);
}

export function checkForExistingWorksheet(worksheetId: string) {
  const existing = getCurrentWorksheet();
  if(existing === worksheetId){
    log.error('Worksheet already open in another tab');
    alert ("This worksheet is already open in another tab. Consider closing the other tab to avoid billing conflicts.");
  } else {
    setCurrentWorksheet(worksheetId);
  }
}


export function getCurrentVisitSession(id: string): Visit {
  return JSON.parse(localStorage.getItem(visitKey(id)))
}

function removeCurrentVisitSession(visit: Visit) {
  if (!visit) return

  const id = visitKey(visit.visitId)
  try {
    window.localStorage.removeItem(id)
    window.localStorage.setItem(id, null)
  } catch (e) {
    console.info('error setting visit session', id, e)
  }
}

export const visitContextApi = createVisitContextApi()
const loadNextVisit = (existing: Visit, error?: Error) => {
  if (existing) {
    removeCurrentVisitSession(existing)
  }
  // Remember why we skipped, so the end of the queue reports the real failure
  // instead of the misleading 'end of the visit list' message.
  skippedVisit = error ? { visit: existing, error } : null
  visitContextApi.getState().load()
}

type SaveWorksheetResult = WorksheetResult<WorksheetComponents>
export const saveWorksheetRequest = fetchRequest<Worksheet, SaveWorksheetResult>({
  method: 'post',
  body: (args: Worksheet) => worksheetMessage(normalizeServiceDates({ ...args }), 'saveworksheet'),
})

// TODO: cors error and invalid data shape
const fetchVisit = fetchRequest<string, WorksheetResult<any>>({
  method: 'post',
  body: (visitId: string) => ({
    service: 'coding-queue',
    action: 'getvisit',
    data: {
      visitId,
    },
  }),
})

export async function getVisit(visitId: string) {
  const r = await fetchVisit(visitId)
  const v = r.result
  return v
}

export async function loadVisitById(visitId: string, readonly = true, worksheetId?: string) {
  const a: Visit = await getVisit(visitId)

  if (a) {
    a.readonly = readonly
    setCurrentVisitSession(a, true, worksheetId || a.wsSummaries[0]?.worksheetId)
    setTimeout(() => {
      history.push('/tool')
    }, 1000)
  }
}

window.onbeforeunload = () => {
  setCurrentVisitSession(null)
}